import { List } from 'immutable'
import * as React from 'react'
import { hyphen } from '../../../Shared/utils/hyphenate'
import { slugify } from '../../../Shared/utils/string'
import { Link } from './library/link'
import { VideoRewriteSrc } from './video_block'

export type TeasersProps = {
  items: List<TeaserProps>
}

export type Category = {
  name: string
  slug: string
}

export type TeaserProps = {
  extraClasses?: string
  linkUrl?: string
} & TeaserContentProps

export type TeaserInnerProps = {
  linkUrl?: string
  teaserContent: JSX.Element
  title: string
  categories?: Category[]
}

export type TeaserContentProps = {
  headingLevel: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  title: string
  isTip?: boolean
  label?: string | JSX.Element
  imageAlt?: string
  imageUrl?: string
  imageUrlLarge?: string
  videoUrl?: string
  intro?: string
  footer?: JSX.Element | string
  locationFooter?: JSX.Element | string
  categories?: Category[]
  key?: string
  linkUrl?: string
}

export const Teasers = (p: TeasersProps): JSX.Element => (
  <ul className="teasers row">
    {p.items.map((i, k) => (
      <Teaser {...i} key={i.key || `teaser-${k}`} />
    ))}
  </ul>
)

export const Teaser = (p: TeaserProps): JSX.Element => (
  <li
    className={`teaser teaser--default ${p.imageUrl ? 'teaser--image' : 'teaser--text'} ${
      p.extraClasses ? p.extraClasses : 'col-xs-12 col-sm-6 col-md-4'
    }`}
  >
    {TeaserInner({ linkUrl: p.linkUrl, teaserContent: TeaserContent(p), title: p.title, categories: p.categories })}
  </li>
)

export const TeaserInner = (p: TeaserInnerProps) =>
  p.linkUrl ? (
    <Link url={p.linkUrl} className="teaser__link" aria-labelledby={`a11y-teaser-title-${slugify(p.title)}`}>
      {p.teaserContent}
    </Link>
  ) : (
    <div className="teaser__link">{p.teaserContent}</div>
  )

export const TeaserContent = (p: TeaserContentProps) => {
  const Heading = p.headingLevel
  return (
    <article className={`teaser__inner${p.label ? ' has-label' : ''}${p.videoUrl ? ' has-video' : ''}`}>
      {p.linkUrl && (
        <div className="button button--secondary teaser__button">
          <span className="button__label">Lees verder</span>
        </div>
      )}
      <div className="teaser__content">
        {p.label && (
          <div className="teaser__meta">
            <div className="teaser__label">{p.label}</div>
          </div>
        )}

        <header className="teaser__header">
          <Heading className="teaser__title" id={`a11y-teaser-title-${slugify(p.title)}`}>
            <span dangerouslySetInnerHTML={{ __html: hyphen(p.title) }} />
          </Heading>
          {p.isTip && (
            <>
              <span className="teaser__tip">Tip</span>
              <span className="sr-text"> van de redactie</span>
            </>
          )}
        </header>

        <div className="teaser__text">
          {p.intro && <p className="teaser__intro" dangerouslySetInnerHTML={{ __html: p.intro }} />}
          {p.locationFooter && (
            <footer className="teaser__footer text--xs">
              <svg className="teaser__footer-icon" width="20px" height="24px" aria-hidden="true">
                <use xlinkHref="#icon--pin-dnb" />
              </svg>
              <span className="sr-text">Locatie: </span>
              {p.locationFooter}
            </footer>
          )}
          {p.footer && <footer className="teaser__footer">{p.footer}</footer>}
        </div>
        {p.categories && (
          <div className="teaser__tags tags">
            <ul className="tags__list">
              {p.categories.map(category => (
                <li className="tags__item" key={`blog_posts_category_item_${category.slug}`}>
                  <span className="tags__tag">{category.name}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {p.imageUrl && (
        <div className="teaser__visual">
          <img
            key={p.imageUrl}
            src="/bkw/images/bkw/empty.png"
            data-src={p.imageUrl}
            data-srcset={p.imageUrlLarge ? `${p.imageUrl} 400w, ${p.imageUrlLarge} 800w` : undefined}
            width="400"
            height="280"
            alt={p.imageAlt}
            className="teaser__image lazyload"
          />
        </div>
      )}

      {p.videoUrl && (
        <div className="video-block__wrapper teaser__video">
          <div className="video-block__inner">
            <iframe
              className="video-block__iframe teaser__iframe"
              key="iframe"
              src={VideoRewriteSrc({
                autoplay: false,
                controls: true,
                info: true,
                videoName: p.title,
                videoUrl: p.videoUrl
              })}
              frameBorder="0"
              title={p.title}
            />
          </div>
        </div>
      )}
    </article>
  )
}

export const TeaserHasLinkContent = (p: TeaserContentProps) => {
  const Heading = p.headingLevel
  return (
    <article className="teaser__inner">
      <div className="teaser__content">
        <header className="teaser__header">
          <Heading className="teaser__title" id={`a11y-teaser-title-${slugify(p.title)}`}>
            <span dangerouslySetInnerHTML={{ __html: hyphen(p.title) }} />
          </Heading>
        </header>
        <div className="teaser__text">
          {p.intro && <div className="teaser__intro" dangerouslySetInnerHTML={{ __html: p.intro }} />}
          {p.footer && (
            <footer className="teaser__footer text--xs">
              <span className="link link--primary link--arrow">{p.footer}</span>
            </footer>
          )}
        </div>
      </div>

      {p.imageUrl && (
        <div className="teaser__visual">
          <img
            key={p.imageUrl}
            src="/bkw/images/bkw/empty.png"
            data-src={p.imageUrl}
            data-srcset={p.imageUrlLarge ? `${p.imageUrl} 400w, ${p.imageUrlLarge} 800w` : undefined}
            width="400"
            height="280"
            alt={p.imageAlt}
            className="teaser__image lazyload"
          />
        </div>
      )}
    </article>
  )
}

export const SliderTeaserContent = (p: TeaserContentProps) => {
  const Title = p.headingLevel
  return (
    <article className={`teaser__inner${p.label ? ' has-label' : ''}${p.videoUrl ? ' has-video' : ''}`}>
      <div className="teaser__content">
        {p.label && (
          <div className="teaser__meta">
            <div className="teaser__label">{p.label}</div>
          </div>
        )}

        <header className="teaser__header">
          <Title className="teaser__title">
            {p.isTip && <span className="teaser__tip">TIP</span>}
            <span>{hyphen(p.title)}</span>
          </Title>
        </header>

        <div className="teaser__text">
          {p.intro && <p className="teaser__intro" dangerouslySetInnerHTML={{ __html: p.intro }} />}
          {p.locationFooter && (
            <footer className="teaser__footer text--xs">
              <svg className="teaser__footer-icon" width="20px" height="24px" aria-hidden="true">
                <use xlinkHref="#icon--pin-dnb" />
              </svg>
              {p.locationFooter}
            </footer>
          )}
        </div>
      </div>
      {p.imageUrl && (
          <div className="teaser__visual">
            <img
              key={p.imageUrl}
              src="/bkw/images/bkw/empty.png"
              data-src={p.imageUrl}
              data-srcset={p.imageUrlLarge ? `${p.imageUrl} 400w, ${p.imageUrlLarge} 800w` : undefined}
              width="400"
              height="280"
              alt={p.imageAlt}
              className="teaser__image lazyload"
            />
          </div>
        )}

        {p.videoUrl && (
          <div className="video-block__wrapper teaser__video">
            <div className="video-block__inner">
              <iframe
                className="video-block__iframe teaser__iframe"
                key="iframe"
                src={VideoRewriteSrc({
                  autoplay: false,
                  controls: true,
                  info: true,
                  videoName: p.title,
                  videoUrl: p.videoUrl
                })}
                frameBorder="0"
                title={p.title}
              />
            </div>
          </div>
        )}
    </article>
  )
}

export const SliderTeaser = (p: TeaserProps): JSX.Element => (
  <div className="teaser teaser--default teaser--image teaser--carousel">
    {TeaserInner({ linkUrl: p.linkUrl, teaserContent: SliderTeaserContent(p), title: p.title })}
  </div>
)

{
  /* TH: Added to styleguide! */
}
